import { useState, useEffect, useCallback } from "react";

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: "accepted" | "dismissed";
    platform: string;
  }>;
  prompt(): Promise<void>;
}

declare global {
  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

const INSTALL_STATUS_KEY = "pwa_installed_status";

export function usePWA() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if running in standalone mode (already installed)
    const checkStandalone = () => {
      // Multiple detection methods for better compatibility
      const displayModeStandalone = window.matchMedia("(display-mode: standalone)").matches;
      const navigatorStandalone = (window.navigator as any).standalone === true;
      const displayModeMinimalUI = window.matchMedia("(display-mode: minimal-ui)").matches;
      const displayModeFullscreen = window.matchMedia("(display-mode: fullscreen)").matches;
      
      // Check if opened from home screen (iOS specific)
      const isHomeScreenLaunch = document.referrer === "" && !window.opener;
      
      // Check localStorage for previous installation confirmation
      const previouslyInstalled = localStorage.getItem(INSTALL_STATUS_KEY) === "true";
      
      const standalone = displayModeStandalone || 
        navigatorStandalone || 
        displayModeMinimalUI || 
        displayModeFullscreen;
      
      setIsStandalone(standalone);
      
      // If currently standalone, mark as installed
      if (standalone) {
        localStorage.setItem(INSTALL_STATUS_KEY, "true");
        setIsInstalled(true);
      } else {
        // Only use localStorage as fallback for iOS where detection is unreliable
        // On iOS, if user previously confirmed install AND they're launching from home screen context
        const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
        if (isIOSDevice && previouslyInstalled && isHomeScreenLaunch) {
          setIsInstalled(true);
        } else if (!isIOSDevice) {
          // For non-iOS, trust the browser's detection
          setIsInstalled(standalone);
        }
      }
    };

    checkStandalone();

    // Check if iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    setIsIOS(isIOSDevice);

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: BeforeInstallPromptEvent) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    // Listen for app installed event
    const handleAppInstalled = () => {
      localStorage.setItem(INSTALL_STATUS_KEY, "true");
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
    };

    // Listen for display mode changes
    const mediaQuery = window.matchMedia("(display-mode: standalone)");
    const handleDisplayModeChange = (e: MediaQueryListEvent) => {
      if (e.matches) {
        localStorage.setItem(INSTALL_STATUS_KEY, "true");
        setIsStandalone(true);
        setIsInstalled(true);
      }
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);
    mediaQuery.addEventListener("change", handleDisplayModeChange);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleAppInstalled);
      mediaQuery.removeEventListener("change", handleDisplayModeChange);
    };
  }, []);

  const install = useCallback(async (): Promise<boolean> => {
    if (!deferredPrompt) return false;

    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === "accepted") {
        localStorage.setItem(INSTALL_STATUS_KEY, "true");
        setIsInstalled(true);
        setIsInstallable(false);
      }

      setDeferredPrompt(null);
      return outcome === "accepted";
    } catch (error) {
      console.error("Error installing PWA:", error);
      return false;
    }
  }, [deferredPrompt]);

  // Manual confirmation for iOS users (called after they follow the instructions)
  const confirmIOSInstall = useCallback(() => {
    localStorage.setItem(INSTALL_STATUS_KEY, "true");
    setIsInstalled(true);
  }, []);

  // Reset installation status (for debugging or if user uninstalled)
  const resetInstallStatus = useCallback(() => {
    localStorage.removeItem(INSTALL_STATUS_KEY);
    setIsInstalled(false);
  }, []);

  return {
    isInstallable,
    isInstalled,
    isIOS,
    isStandalone,
    install,
    confirmIOSInstall,
    resetInstallStatus,
  };
}
